
import * as angular from "angular";
import "angular-material";

import { IKeyValuePair } from "@interfaces/IKeyValuePair";
import { FormBuilderService } from "../formBuilder.service";
import { IForm } from "../IForm";
import { IFormDetail } from "../IFormDetail";
import { IFormUiDetail } from "./IFormUiDetail";
import { IOptionItem } from "./IOptionItem";

import "./updateFormDetails.scss";

interface IUpdateFormDetailsComponentBindings {
    inputForm: IForm;
    fieldTypes: IKeyValuePair[];
    autoDataFields: IKeyValuePair[];
    addMode: boolean;
    jobCompletionTypeFormId: number,
    onUpdate: (args: { form: IForm, addMode: boolean }) => void;
    onCancel: (args: { changesMade: boolean }) => void;
    onDelete: (args: { form: IForm }) => void;
}

interface ICatMap {
    category: string;
    formDetails: IFormUiDetail[];
}

class UpdateFormDetailsController implements angular.IComponentController, IUpdateFormDetailsComponentBindings {
    inputForm: IForm;
    formTypes: IKeyValuePair[];
    fieldTypes: IKeyValuePair[];
    autoDataFields: IKeyValuePair[];

    form: IForm;
    addMode: boolean;
    jobCompletionTypeFormId: number;

    catMaps: ICatMap[];

    fieldType = -1;
    selectedFormId = 0;
    selectedTabIndex: number;
    selectedCategoryTab: string;

    changesMade = false;

    categoryFormInvalid: boolean[];

    fieldTypeOldLabel = 1;
    fieldTypeOldInput = 2;
    fieldTypeCheckbox = 3;
    fieldTypeRadioGroup = 4;
    fieldTypeSwitch = 5;
    fieldTypeComboList = 6;
    fieldTypeInput = 7;
    fieldTypeDatePicker = 11;
    fieldTypeTimePicker = 12;
    fieldTypeCustomVehicleCheck = 16;
    fieldTypeLocationStamp = 20;
    fieldTypeDateStamp = 21;
    fieldTypeAuto = 25;

    initComplete = false;

    onUpdate: (args: { form: IForm, addMode: boolean }) => void;
    onCancel: (args: { changesMade: boolean }) => void;
    onDelete: (args: { form: IForm }) => void;

    static $inject = ["formBuilderService", "$mdDialog"];
    constructor(private readonly formBuilderService: FormBuilderService,
        private readonly $mdDialog: angular.material.IDialogService) {
        this.catMaps = [];
        this.categoryFormInvalid = [];
        this.formTypes = [];
        this.changesMade = false;
        this.selectedFormId = 0;
        this.selectedTabIndex = 0;
        this.selectedCategoryTab = "";
    }

    $onChanges(): void {
        this.form = angular.copy(this.inputForm);
        this.populateUiModelFromDbModel();
        this.formTypes = this.getFormTypes();
        this.initFormArrays();
        this.selectedTabIndex = 0;
        this.selectedCategoryTab = this.catMaps.length > 0 ? this.catMaps[0].category : "";
        if (this.addMode) {
            this.createAddModeInitialState();
        }
    }

    onTabChanged(tabIndex: number, tabTitle: string) {
        this.selectedTabIndex = tabIndex;
        this.selectedCategoryTab = tabTitle;
    }

    get isAddModeInitialState(): boolean {
        return this.addMode && this.catMaps && this.catMaps.length === 1 && this.catMaps[0].category === " " &&
            this.catMaps[0].formDetails.length === 0;
    }

    get formStatusIsInvalid(): boolean {
        return this.categoryFormInvalid.some(status => this.isInvalidStatus(status));
    }

    get missingFields(): boolean {
        let missingFields = false;

        this.catMaps.forEach(catMap => {
            missingFields = missingFields || !catMap.formDetails || catMap.formDetails.length < 1;
            missingFields = missingFields || this.hasInvalidOptions(catMap);
        });

        return missingFields;
    }

    get hasMaxCats(): boolean {
        const maximumCategories = 6;
        return this.catMaps && this.catMaps.length >= maximumCategories;
    }

    get nonWhitespaceChar(): any {
        return new RegExp(".*\\S+.*").source;
    }

    showDuplicateCategoriesDialog() {
        const duplicateCategoriesDialog = this.$mdDialog
            .confirm()
            .title("Duplicate category names not permitted!")
            .ok("Ok");

        this.$mdDialog
            .show(duplicateCategoriesDialog)
            .then(() => {
            });
    }

    saveForm(): void {
        if (this.duplicateCategories()) {
            this.showDuplicateCategoriesDialog();
            return;
        }

        this.populateDbModelFromUiModel();

        this.onUpdate({ form: this.form, addMode: this.addMode });
    }

    private duplicateCategories(): boolean {
        if (!this.catMaps) return false;

        const catMaps = this.catMaps;

        let duplicateCategories = false;

        let catIdx = 0;

        catMaps.forEach(catMap => {
            for (let idx = catIdx - 1; idx >= 0; idx--) {
                if (catMaps[idx].category.trim().toLowerCase() === catMap.category.trim().toLowerCase()) {
                    duplicateCategories = true;
                }
            }
            catIdx++;
        });

        return duplicateCategories;
    }

    deleteForm() {
        this.onDelete({ form: this.form });
    };

    cancelChanges(formChanged = false) {
        const formWasChanged = formChanged || this.changesMade;
        this.onCancel({ changesMade: formWasChanged });
    };

    addFormField(catMap: ICatMap, catIdx: number) {
        const cat = catMap && catMap.category ? catMap.category : "";
        catMap.formDetails.push(this.createNewFormUiDetail(cat));
        this.categoryDetailsChanged(true, catIdx);
    };

    removeField(catMap: ICatMap, catIdx: number, formDetailIdx: number, formIsInvalid: boolean): void {
        catMap.formDetails.splice(formDetailIdx, 1);
        const categoryIsInvalid = formIsInvalid && this.isCategoryDataInvalid(catMap);
        if (formIsInvalid && !categoryIsInvalid) {
            this.categoryDetailsChanged(categoryIsInvalid, catIdx);
        }
    }

    removeCategory(catIdx: number): void {
        this.categoryFormInvalid.splice(catIdx, 1);
        this.catMaps.splice(catIdx, 1);
        if (this.catMaps.length > 0) {
            this.selectedTabIndex = this.catMaps.length - 1;
            this.selectedCategoryTab = this.catMaps[this.catMaps.length - 1].category;
        } else {
            this.selectedTabIndex = 0;
            this.selectedCategoryTab = "";
        }
    }

    isAddCategoryDisabled(): boolean {
        for (let catMap of this.catMaps) {
            if (this.isCategoryDataInvalid(catMap)) return true;
        }

        return false;
    }

    getNewCategoryName(): string {
        const newCategoryName = "New Category";
        let categoryName: string;
        let iterationIdx = 0;
        let nameInUse;
        do {
            categoryName = iterationIdx === 0 ? newCategoryName : `${newCategoryName} (${iterationIdx})`;
            nameInUse = false;
            this.catMaps.forEach(c => {
                if (c.category === categoryName) {
                    nameInUse = true;
                }
            });
            iterationIdx++;
        } while (nameInUse);

        return categoryName;
    }

    addCategory() {
        if (this.isAddModeInitialState) {
            this.catMaps.length = 0;
        }

        const newCategoryName = this.getNewCategoryName();
        const catMap = {
            category: newCategoryName,
            formDetails: []
        };
        catMap.formDetails.push(this.createNewFormUiDetail(""));
        this.catMaps.push(catMap);
        this.selectedTabIndex = this.catMaps.length - 1;
        this.selectedCategoryTab = newCategoryName;

        this.categoryFormInvalid.push(true);
    }

    categoryDetailsChanged(formIsInvalid: boolean, categoryIdx: number) {
        this.categoryFormInvalid[categoryIdx] = formIsInvalid;
        this.changesMade = true;
    }

    fieldTypeChanged(formItem: IFormUiDetail, formIsInvalid: boolean, categoryIdx: number) {
        if (this.isMultiOptionFieldType(formItem)) {
            if (!formItem.optionItems) {
                formItem.optionItems = [];
            }
            if (formItem.optionItems.length === 0) {
                formItem.optionItems.push({ optionLabel: "", checked: false });
            }
            if (formItem.formDetail.Type === this.fieldTypeSwitch) {
                if (formItem.optionItems.length < 2) {
                    formItem.optionItems.push({ optionLabel: "", checked: false });
                } else if (formItem.optionItems.length > 2) {
                    formItem.optionItems.splice(2);
                }
            }
        }
        this.categoryDetailsChanged(formIsInvalid, categoryIdx);
    }

    removeOptionField(formItem: IFormUiDetail, optionIndex: number) {
        formItem.optionItems.splice(optionIndex, 1);
    }

    addOptionField(formItem: IFormUiDetail) {
        formItem.optionItems.push({ optionLabel: "", checked: false });
    }

    getNameWithIndex(name: string, idx: number) {
        return name + idx;
    }

    isAddOptionDisabled(formItem: IFormUiDetail): boolean {
        let invalid = false;

        if (!formItem || !formItem.optionItems) {
            return false;
        }

        formItem.optionItems.forEach((optionItem: IOptionItem) => {
            invalid = invalid || !optionItem.optionLabel
                || !optionItem.optionLabel.trim()
                || optionItem.optionLabel.length > 200;
        });

        return invalid;
    }

    tabTitle(catMap: ICatMap): string {
        let category = (catMap.category || "").trim();
        return category || '(Unnamed)';
    }

    hideCategory(catMap: ICatMap): boolean {
        return this.addMode && catMap.category === " " && catMap.formDetails.length === 0;
    }

    showRequired(fieldType: number) {
        let show = true;

        switch (fieldType) {
            case this.fieldTypeCheckbox:
            case this.fieldTypeSwitch:
            case this.fieldTypeDatePicker:
            case this.fieldTypeTimePicker:
            case this.fieldTypeCustomVehicleCheck:
                show = false;
        }

        return show;
    }

    showTodayOption(fieldType: number): boolean {
        return fieldType === this.fieldTypeDateStamp;
    }

    hideRow(formItem: IFormUiDetail): boolean {
        return formItem && formItem.formDetail && formItem.formDetail.Type === this.fieldTypeOldLabel;
    }

    showFieldType(formItem: IFormUiDetail): boolean {
        return formItem && formItem.formDetail && formItem.formDetail.Type !== this.fieldTypeOldLabel;
    }

    isSwitch(formItem: IFormUiDetail): boolean {
        return formItem && formItem.formDetail && formItem.formDetail.Type === this.fieldTypeSwitch;
    }

    optionLabel(optionIndex: number, formItem: IFormUiDetail): string {
        const optionLabel = "Option Label " + +(optionIndex + 1);

        if (this.isSwitch(formItem) && optionIndex === 0) {
            return optionLabel + " (Default)";
        }

        return optionLabel;
    }

    minCategories(): boolean {
        return this.catMaps && this.catMaps.length < 2;
    }

    minFormFields(catIdx: number) {
        return !this.catMaps || !this.catMaps[catIdx] || !this.catMaps[catIdx].formDetails || this.catMaps[catIdx].formDetails.length < 2;
    }

    private createAddModeInitialState() {
        this.catMaps.length = 0;
        this.selectedTabIndex = 0;
        this.selectedCategoryTab = " ";

        const category = " ";

        const formUiDetails = <IFormUiDetail[]>[];

        formUiDetails.push(this.createNewFormUiDetail(category));

        this.catMaps.push({ category: category, formDetails: formUiDetails });
    }

    private getFormTypes() {
        const enableJobCompletionType = this.jobCompletionTypeFormId < 0 || this.form?.Id === this.jobCompletionTypeFormId;
        return this.formBuilderService.getFormTypes(enableJobCompletionType);
    }

    private populateDbModelFromUiModel(): void {
        this.form.FormFields = [];

        const formFields = this.form.FormFields;

        this.catMaps.forEach(catMap => {
            catMap.formDetails.forEach(fd => {
                const formDetail = this.mapUiToDbModel(fd);
                formDetail.Category = catMap.category;
                formFields.push(formDetail);
            });
        });

        this.form.Enabled = true;
    }

    private populateUiModelFromDbModel() {
        this.catMaps = [];

        if (this.addMode || !this.form || !this.form.FormFields) {
            return;
        }

        const categoryMaps = new Map<string, IFormUiDetail[]>();

        // this loop creates the catMap entries in the incoming sequence and collates the formDetails for each category 
        this.form.FormFields.forEach(fieldItem => {
            const category = fieldItem.Category ? fieldItem.Category.trim() : "";

            if (!categoryMaps.has(category)) {
                categoryMaps.set(category, <IFormUiDetail[]>[]);
                const catMap = {
                    category: category,
                    formDetails: []
                };
                this.catMaps.push(catMap);
            }

            categoryMaps.get(category).push(this.mapDbToUiModel(fieldItem));
        });

        // this loop outputs the consolidated formDetails to the catMap array
        this.catMaps.forEach((catMap: ICatMap) => {
            const category = catMap.category;
            const formDetails = categoryMaps.get(category);
            formDetails.forEach((formDetail: IFormUiDetail) => {
                catMap.formDetails.push(formDetail);
            });
        });
    }

    private setValidationField(formUiDetail: IFormUiDetail): string {
        let validation = formUiDetail.required ? "*|" : "";

        if (formUiDetail.inputFilterTypeNumeric) {
            validation += "0-9|";
        }

        if (formUiDetail.formDetail.Type === this.fieldTypeAuto || formUiDetail.formDetail.Type === this.fieldTypeLocationStamp) {
            validation += "DISABLE|";
        }

        if (formUiDetail.isBold) {
            validation += "BOLD|";
        }

        if (formUiDetail.defaultToToday) {
            validation += "TODAY|";
        }

        return (validation.length < 3) ? validation : validation.replace(/|$/, "");
    }

    // switch the order to make the default option the second option
    private getSwitchOptions(optionItems: IOptionItem[]) {
        return optionItems[1].optionLabel + "|" + optionItems[0].optionLabel;
    }

    private setDefaultField(formUiDetail: IFormUiDetail): string {
        let defaultField = "";

        if (formUiDetail.optionItems && formUiDetail.optionItems.length > 0) {
            if (formUiDetail.formDetail.Type === this.fieldTypeSwitch && formUiDetail.optionItems.length === 2) {
                defaultField += this.getSwitchOptions(formUiDetail.optionItems);
            } else {
                formUiDetail.optionItems.forEach((optionItem: IOptionItem) => {
                    if (defaultField.length > 0) defaultField += "|";
                    defaultField += optionItem.optionLabel;
                });
            }
        }

        return defaultField;
    }

    private convertUiToDbAutoLookupTypes(formDetail: IFormDetail) {
        if (formDetail.Type === this.fieldTypeAuto) {
            switch (+formDetail.Name) {
                case 1:
                    formDetail.Name = "JOB ID";
                    formDetail.isIdField = true;
                    break;
                case 2:
                    formDetail.Name = "JOB NAME";
                    break;
                case 3:
                    formDetail.Name = "JOB VEHICLE NAME";
                    break;
                case 4:
                    formDetail.Name = "JOB VEHICLE REG";
                    break;
            }

            formDetail.Type = this.fieldTypeInput;
        }
    }

    private mapUiToDbModel(formUiDetail: IFormUiDetail): IFormDetail {
        const formDetail = angular.copy(formUiDetail.formDetail);
        this.convertUiToDbAutoLookupTypes(formDetail);
        formDetail.Validation = this.setValidationField(formUiDetail);
        formDetail.DefaultValue = this.setDefaultField(formUiDetail);

        return formDetail;
    }

    private setUiValidationFields(formUiDetail: IFormUiDetail) {
        const validation = formUiDetail.formDetail.Validation;

        formUiDetail.required = validation.indexOf("*|") >= 0;
        formUiDetail.inputFilterTypeNumeric = validation.indexOf("0-9") >= 0;
        formUiDetail.isBold = validation.indexOf("BOLD") >= 0;
        formUiDetail.defaultToToday = validation.indexOf("TODAY") >= 0;
    }

    private setUiDefaultFields(formUiDetail: IFormUiDetail): void {
        const defaultValue = formUiDetail.formDetail.DefaultValue;
        if (!defaultValue)
            return;

        const defaultFields = formUiDetail.formDetail.DefaultValue.split("|");

        if (defaultFields.length < 1) return;

        if (this.isSwitch(formUiDetail) && defaultFields.length > 1) {
            formUiDetail.optionItems.push({ optionLabel: defaultFields[1], checked: false });
            formUiDetail.optionItems.push({ optionLabel: defaultFields[0], checked: false });
            return;
        }

        defaultFields.forEach(option => {
            formUiDetail.optionItems.push({ optionLabel: option, checked: false });
        });
    }

    private isAutoField(formDetail: IFormDetail) {
        return (formDetail.Type === this.fieldTypeInput ||
            formDetail.Type === this.fieldTypeOldInput) &&
            formDetail.Name.startsWith("JOB") &&
            (formDetail.Name === "JOB ID" ||
                formDetail.Name === "JOB NAME" ||
                formDetail.Name === "JOB VEHICLE REG" ||
                formDetail.Name === "JOB VEHICLE NAME");
    }

    private convertDbAutoLookupTypes(formDetail: IFormDetail) {
        if (this.isAutoField(formDetail)) {
            switch (formDetail.Name) {
                case "JOB ID":
                    formDetail.Name = "1";
                    break;
                case "JOB NAME":
                    formDetail.Name = "2";
                    break;
                case "JOB VEHICLE NAME":
                    formDetail.Name = "3";
                    break;
                case "JOB VEHICLE REG":
                    formDetail.Name = "4";
                    break;
            }
            formDetail.Type = this.fieldTypeAuto;
        }
    }

    private mapDbToUiModel(formDetail: IFormDetail): IFormUiDetail {
        this.convertDbAutoLookupTypes(formDetail);

        const formUiDetail: IFormUiDetail = {
            formDetail: angular.copy(formDetail),
            required: false,
            isBold: false,
            inputFilterTypeNumeric: false,
            defaultToToday: false,
            optionItems: <IOptionItem[]>[]
        };

        this.setUiValidationFields(formUiDetail);
        this.setUiDefaultFields(formUiDetail);

        return formUiDetail;
    }

    private isInvalidStatus(status) {
        return status;
    }

    private hasInvalidOptions(catMap: ICatMap): boolean {
        let invalid = false;

        catMap.formDetails.forEach((f: IFormUiDetail) => {
            invalid = invalid || !f.formDetail || !f.formDetail.Type || !f.formDetail.Name;

            if (!invalid) {
                switch (f.formDetail.Type) {
                    case this.fieldTypeComboList:
                    case this.fieldTypeRadioGroup:
                    case this.fieldTypeSwitch:
                        invalid = invalid ||
                            f.optionItems.length < 1 ||
                            f.formDetail.Type === this.fieldTypeSwitch && f.optionItems.length !== 2;
                        if (!invalid) {
                            f.optionItems.forEach(optionLabel => {
                                invalid = invalid || !optionLabel;
                            });
                        }
                }
            }
        });

        return invalid;
    }

    private isCategoryDataInvalid(catMap: ICatMap): boolean {
        if (!catMap || !catMap.category) {
            return true;
        }

        if (typeof catMap.formDetails[0] !== "undefined" && !catMap.formDetails[0]) {
            return true;
        }

        let invalid = this.hasInvalidOptions(catMap);

        catMap.formDetails.forEach((f: IFormUiDetail) => {
            invalid = invalid || !f.formDetail || !f.formDetail.Type || !f.formDetail.Name;

            if (!invalid) {
                f.optionItems.forEach((o: IOptionItem) => {
                    invalid = invalid || !o.optionLabel || o.optionLabel.length > 200;
                });
            }

            invalid = invalid || this.hasInvalidOptions(catMap);
        });

        return invalid;
    }

    private initFormArrays() {
        this.categoryFormInvalid = [];
        this.catMaps.forEach(() => {
            this.categoryFormInvalid.push(false);
        });
    }

    private createNewFormUiDetail(cat: string): IFormUiDetail {
        const formDetail: IFormDetail = {
            FieldId: 0,
            Id: 0,
            Type: this.fieldTypeInput,
            Name: "",
            description: "",
            Validation: "",
            position: 0,
            Category: cat,
            DefaultValue: "",
            isIdField: false
        };
        const formUiDetail: IFormUiDetail = {
            formDetail: formDetail,
            required: true,
            isBold: false,
            inputFilterTypeNumeric: false,
            defaultToToday: false,
            optionItems: []
        }
        return formUiDetail;
    }

    private isMultiOptionFieldType(formItem: IFormUiDetail) {
        return formItem.formDetail.Type === this.fieldTypeRadioGroup ||
            formItem.formDetail.Type === this.fieldTypeSwitch ||
            formItem.formDetail.Type === this.fieldTypeComboList;
    }
}

export class UpdateFormDetailsComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
            inputForm: "<",
            fieldTypes: "<",
            autoDataFields: "<",
            addMode: "<",
            jobCompletionTypeFormId: "<",
            onCancel: "&",
            onUpdate: "&",
            onDelete: "&"
        };

        this.controller = UpdateFormDetailsController;
        this.template = require("./updateFormDetails.html");
    }
}
